import classNames from 'classnames'
import { useCallback, useState } from 'react'
import { Form } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import { addPlayerManual, postEmail, updatePlayerManual } from '../../../../../api/gameApiService'
import { TextAreaFormField } from '../../../../../common/components/Form/TextAreaFormField/TextAreaFormField'
import { Button } from '../../../../../common/components/button/Button'
import { CloseButton } from '../../../../../common/components/button/CloseButton'
import { getIcon } from '../../../../../common/components/icons/utils'
import { useBusinessSettings } from '../../../../../contexts/BusinessSettingsContext'
import { useGame } from '../../../../../contexts/GameContext'
import { useNotification } from '../../../../../contexts/NotificationContext'
import { useUser } from '../../../../../contexts/userContext'
import { AllowedOption, FormErrorType, Player } from '../../../../../types/commonTypes'
import { hasPermission } from '../../../../../util/permissions'
import { safeIsNullOrEmpty } from '../../../../../util/string'
import { lowerThanOrEqualToValidation, requiredValidation } from '../../../../../util/validate'
import styles from '../AddPerson.module.css'
import { PopupActionMenu, PopupActionMenuItem } from '../../../../../common/components/PopupMenu/PopupActionMenu'
import { AddPlayerForm, PlayerModalMode, PlayersAddManuallyModal } from './PlayersAddManuallyModal'
import { StartingTaskSelection } from './StartingTaskSelection'

type PlayersMenuProps = {
  gameId: number
  onManageAnswersClick?: () => void
  onDelete: (id: number, name: string) => void
}

type PlayerEmailForm = {
  emails: string
  showStartingTaskSelection: boolean
  startingTask?: number
}

const re =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

const isDev = process.env.REACT_APP_ENV === 'development'

export const PlayersMenu: React.FC<PlayersMenuProps> = ({ gameId, onManageAnswersClick, onDelete }) => {
  const { people, isOpen, tasks, gameData } = useGame()
  const isOrdered = !!gameData?.advancedSettings.orderingEnabled
  const { data } = useBusinessSettings()
  const { user } = useUser()
  const [isInputOpen, setIsInputOpen] = useState<boolean>(false)
  const { notifyError } = useNotification()
  const { t } = useTranslation()
  const players = people.players
  const playerLicenseCount = data?.businessInfo.newBusinessInfo?.licenceCount
  const usedPlayerLicenseCount =
    (data?.businessInfo?.newBusinessInfo?.usedPinSeats || 0) +
    (data?.businessInfo?.newBusinessInfo?.usedPlayerAccountSeats || 0)
  const maxPlayers = data?.businessInfo.maxPlayers
  const [addManuallyModalState, setAddManuallyModalState] = useState(PlayerModalMode.CLOSED)
  const [editPlayer, setEditPlayer] = useState<Player | undefined>()

  const handleInputField = () => {
    setIsInputOpen((prev) => !prev)
  }

  const openAddPlayerModal = () => {
    setAddManuallyModalState(PlayerModalMode.NEW)
  }

  const openEditPlayerModal = useCallback(
    (player: Player) => () => {
      setEditPlayer(player)
      setAddManuallyModalState(PlayerModalMode.FULL_EDIT)
    },
    [],
  )

  const openEditStartingTaskModal = useCallback(
    (player: Player) => () => {
      setEditPlayer(player)
      setAddManuallyModalState(PlayerModalMode.STARTING_TASK)
    },
    [],
  )

  const handleCloseAddManuallyModal = useCallback(() => {
    setEditPlayer(undefined)
    setAddManuallyModalState(PlayerModalMode.CLOSED)
  }, [])

  const handleSendEmail = async (values: PlayerEmailForm) => {
    const result = await postEmail({
      gameId,
      added_by_email_users: values.emails,
      startingTaskId: values.startingTask ?? null,
    })
    if (result.success) {
      handleInputField()
    } else {
      console.error(result.error)
      notifyError({
        title: t('game_editor.add_people.failed_to_add_player_by_email_notification.title', 'Failed to add players'),
        content: t(
          'game_editor.add_people.failed_to_add_player_by_email_notification.content',
          'An error occurred while adding players by email. Please try again or contact us for support.',
        ),
        timeout: 3000,
      })
    }
  }

  const handleAddPlayer = async (values: AddPlayerForm) => {
    const result = await addPlayerManual({
      gameId,
      nickName: values.nickName,
      teamMemberNames: values.teamMemberNames,
      startTask: values.showStartingTaskSelection ? values.startingTask : undefined,
    })
    if (result.success) {
      if (result.value.error) {
        notifyError({
          title: t('game_editor.add_people.failed_to_add_player_manually_notification.title', 'Failed to add player'),
          content: result.value.msg,
        })
      }
      setEditPlayer(undefined)
      setAddManuallyModalState(PlayerModalMode.CLOSED)
    } else {
      notifyError({
        title: t('game_editor.add_people.failed_to_add_player_manually_notification.title', 'Failed to add player'),
        content: t(
          'game_editor.add_people.failed_to_add_player_manually_notification.content',
          'An error occurred while adding player. Please try again or contact us for support.',
        ),
        timeout: 3000,
      })
    }
  }

  const handleUpdatePlayer = async (values: AddPlayerForm) => {
    const result = await updatePlayerManual({
      gameId,
      userId: editPlayer?.id ?? 0,
      nickName: values.nickName,
      email: editPlayer && !editPlayer.isEmailTrusted ? editPlayer.email : '', //untrusted email needs to be saved in BE with teamMemberNames
      teamMemberNames: values.teamMemberNames,
      startTask:
        values.showStartingTaskSelection || addManuallyModalState === PlayerModalMode.STARTING_TASK
          ? values.startingTask
          : undefined,
    })

    if (result.success) {
      setEditPlayer(undefined)
      setAddManuallyModalState(PlayerModalMode.CLOSED)
    } else {
      console.error(result.error)
      notifyError({
        title: t(
          'game_editor.add_people.failed_to_update_player_manually_notification.title',
          'Failed to update player',
        ),
        content: t(
          'game_editor.add_people.failed_to_update_player_manually_notification.content',
          'An error occurred while updating player. Please try again or contact us for support.',
        ),
        timeout: 3000,
      })
    }
  }

  const filteredPlayers = players.filter((player) => !player.deleted)

  const validate = (values: Partial<PlayerEmailForm>): FormErrorType<PlayerEmailForm> => {
    const addedEmails = values.emails?.split(',')
    const validationText = t('game_editor.add_people.player_limit_reached', 'Player limit reached')
    return {
      emails:
        (maxPlayers != null
          ? lowerThanOrEqualToValidation(addedEmails?.length, maxPlayers - filteredPlayers.length, t, validationText)
          : requiredValidation(values.emails, t)) ??
        (addedEmails?.some((email) => !re.test(email.trim()))
          ? t('validation_errors.invalid_email', 'Invalid email')
          : undefined),
    }
  }

  return (
    <>
      {players.length > 0 && onManageAnswersClick != null && (
        <Button variant='outline-normal' className={styles.manageButton} onClick={onManageAnswersClick}>
          {t('game_editor.add_people.manage_players', 'Manage players and task answers')}
        </Button>
      )}
      {!isOpen && (
        <div className={styles.gameNotStartedInfo}>
          {t('game_editor.add_people.game_not_started', 'Game not started')}
        </div>
      )}
      {filteredPlayers?.length ? (
        <ul className={styles.playersList}>
          {filteredPlayers.map((player, index) => {
            const startingTaskName = !safeIsNullOrEmpty(player.startingTask)
              ? tasks.find((t) => t.id === player.startingTask)?.name
              : null
            return (
              <li key={`${player}${index}`} className={styles.listItem}>
                <span className={classNames(styles.indexStyle, styles.playerIndex)}>{index + 1}</span>
                <div className={styles.listContent}>
                  <span className={styles.listItemTitle}>
                    {!safeIsNullOrEmpty(player.nickName) ? player.nickName : player.name}
                  </span>
                  {!safeIsNullOrEmpty(player.playerCode) && !player.isEmailTrusted && (
                    <span className={styles.listItemMidtitle}>{player.playerCode}</span>
                  )}
                  <span className={player.isEmailTrusted ? styles.trustedEmail : styles.notTrustedEmail}>
                    {!safeIsNullOrEmpty(player.email) && player.email}
                  </span>
                  {!safeIsNullOrEmpty(player.teamMemberNames) && (
                    <span className={styles.listItemSubtitle}>{player.teamMemberNames}</span>
                  )}
                  {startingTaskName && (
                    <div className={styles.startingTaskRow}>
                      <span>{getIcon('pinPlain')}</span>
                      <span className={styles.listItemSubtitle}>{startingTaskName}</span>
                    </div>
                  )}
                </div>
                <PopupActionMenu id={`${player.id}_actionsMenu`} position='inline-down'>
                  <PopupActionMenuItem
                    icon='copy'
                    text={t('game_editor.add_people.edit_player', 'Edit player details')}
                    onClick={openEditPlayerModal(player)}
                  />
                  {isOrdered && (
                    <PopupActionMenuItem
                      icon='pinPlain'
                      text={t('game_editor.add_people.define_starting_task', 'Define starting task')}
                      onClick={openEditStartingTaskModal(player)}
                    />
                  )}
                  <PopupActionMenuItem
                    icon='trash'
                    text={t('common.remove', 'Remove')}
                    onClick={() => onDelete(player.id, player.nickName)}
                  />
                </PopupActionMenu>
              </li>
            )
          })}
        </ul>
      ) : (
        <span className={styles.emptyList}>{t('game_editor.add_people.no_players', 'No players added yet')}</span>
      )}
      <div className={styles.divider} />
      <div className={styles.buttonHeader}>
        <span>{t('game_editor.add_people.add_players', 'Add players')}</span>

        {playerLicenseCount != null && (
          <span className={styles.licenceCount}>
            {t('game_editor.add_people.licence_count', {
              defaultValue: '%{licence_count} licences available',
              licence_count: playerLicenseCount - usedPlayerLicenseCount,
            })}
          </span>
        )}
        {maxPlayers && <span className={styles.maxTeams}>{`${filteredPlayers.length}/${maxPlayers}`}</span>}
      </div>
      {!isInputOpen ? (
        <div className={styles.buttonArea}>
          <div className={styles.firstButtons}>
            {hasPermission(user, AllowedOption.ADD_PLAYERS_BY_EMAIL) && (
              <Button onClick={handleInputField} style={{ flex: 1 }}>
                {t('game_editor.add_people.email', 'E-mail')}
              </Button>
            )}
            {isDev && (
              <Button style={{ flex: 1 }} className='hide-outscoped-feature'>
                {t('game_editor.add_people.player_account', 'Player Account')}
              </Button>
            )}
          </div>
          <div className={styles.secondaryButtons}>
            <Button variant='outline-normal' onClick={openAddPlayerModal}>
              {t('game_editor.add_people.manual', 'Add manually')}
            </Button>
          </div>
          {isDev && (
            <div className={classNames('hide-outscoped-feature', styles.secondaryButtons)}>
              <button>
                <span className='iconWrapperBig'>{getIcon('msTeams')}</span>
                {t('game_editor.add_people.ms_teams', 'MS Teams')}
              </button>
              <button>
                <span className='iconWrapperBig'>{getIcon('googleClassroom')}</span>
                {t('game_editor.add_people.classrooms', 'Classrooms')}
              </button>
            </div>
          )}
        </div>
      ) : (
        <Form<PlayerEmailForm> onSubmit={handleSendEmail} validate={validate}>
          {({ handleSubmit, submitting, values }) => (
            <form onSubmit={handleSubmit} className={styles.playerEmailForm}>
              {maxPlayers && filteredPlayers.length >= maxPlayers && (
                <span className={styles.maxPlayerValidationText}>
                  {t('game_editor.add_people.max_teams_validation', 'Maximum player limit reached')}
                </span>
              )}
              <div className={styles.inputFieldContainer}>
                <TextAreaFormField
                  name='emails'
                  label={t('game_editor.add_people.email', 'E-mail')}
                  placeholder={t('game_editor.add_people.player_email_input_placeholder', 'Type e-mail here')}
                  srOnlyLabel
                  rows={2}
                  fieldContainerClassName={styles.emailInput}
                  errorClassName={styles.emailInputError}
                />
                <Button type='submit' disabled={submitting}>
                  {t('game_editor.add_people.add', 'Add')}
                </Button>
                <CloseButton onClick={handleInputField} />
              </div>
              <span className='tiny grey-900'>
                {t('game_editor.add_people.player_email_input_instructions', 'Separate addresses with a comma')}
              </span>
              {gameData?.advancedSettings.orderingEnabled && <StartingTaskSelection tasks={tasks} values={values} />}
            </form>
          )}
        </Form>
      )}
      {addManuallyModalState !== PlayerModalMode.CLOSED && (
        <PlayersAddManuallyModal
          mode={addManuallyModalState}
          player={editPlayer}
          tasks={tasks}
          isOrdered={isOrdered}
          onClose={handleCloseAddManuallyModal}
          onSubmit={addManuallyModalState === PlayerModalMode.NEW ? handleAddPlayer : handleUpdatePlayer}
        />
      )}
    </>
  )
}
